import React, { Suspense, lazy, useState, useEffect, memo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";

import AppNavbar from "./componets/Navbar.jsx";
import Footer from "./componets/Footer";
import ScrollToTop from "./componets/ScrollToTop";

import AdminDashboard from "./pages/Admin/AdminDashboard.jsx";
import Login from "./pages/Admin/Login.jsx";

/* -------------------- Lazy Pages -------------------- */
const Home = lazy(() => import("./pages/Home.jsx"));
const Contact = lazy(() => import("./pages/Contact"));
const AboutPage = lazy(() => import("./pages/AboutPage.jsx"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));

/* -------------------- Private Route -------------------- */
const PrivateRoute = () => {
  const isAuthenticated = Boolean(localStorage.getItem("admin_token"));
  return isAuthenticated ? <Outlet /> : <Navigate to="/admin/login" replace />;
};

/* -------------------- Loading Spinner -------------------- */
const LoadingSpinner = memo(() => (
  <div
    className="d-flex justify-content-center align-items-center"
    style={{ height: "60vh" }}
  >
    <div
      className="spinner-border text-primary"
      role="status"
      style={{ width: "3rem", height: "3rem" }}
    >
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
));

/* -------------------- Shared Page Layout -------------------- */
const PageLayout = memo(({ title, children }) => (
  <div className="container py-5 mt-5">
    <h1 className="fw-bold" style={{ marginTop: "3%", color: "#0062A0" }}>
      {title}
    </h1>

    <hr
      style={{
        width: "50px",
        height: "3px",
        backgroundColor: "#E91E63",
        border: "none",
        opacity: 1,
      }}
    />

    {children}
  </div>
));

/* -------------------- Main App -------------------- */
function App() {
  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    const fetchGlobalData = async () => {
      try {
        const response = await fetch(
          "https://radheyelectrical.com/api/api_handler.php"
        );

        const json = await response.json();

        if (json.status === "success") {
          setApiData(json.data);
        }
      } catch (error) {
        console.error("Global API fetch error:", error);
      }
    };

    fetchGlobalData();
  }, []);

  return (
    <Router>
      <ScrollToTop />

      {/* Navbar */}
      <AppNavbar
        contactInfo={apiData?.contact_info || []}
        socialLinks={apiData?.social_link || []}
      />

      <main style={{ marginTop: "80px" }}>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />

            <Route
              path="/about"
              element={
                <PageLayout title="About Us">
                  <AboutPage />
                </PageLayout>
              }
            />

            <Route
              path="/services"
              element={
                <PageLayout title="Our Services">
                  <ServicesPage />
                </PageLayout>
              }
            />

            <Route
              path="/projects"
              element={
                <PageLayout title="Our Projects">
                  <ProjectsPage />
                </PageLayout>
              }
            />

            <Route
              path="/gallery"
              element={
                <PageLayout title="Gallery">
                  <GalleryPage />
                </PageLayout>
              }
            />

            <Route
              path="/careers"
              element={
                <PageLayout title="Careers">
                  <CareersPage />
                </PageLayout>
              }
            />

            <Route path="/contact" element={<Contact />} />

            {/* Admin Login */}
            <Route path="/admin/login" element={<Login />} />

            {/* Private Admin Routes */}
            <Route element={<PrivateRoute />}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
            </Route>
          </Routes>
        </Suspense>
      </main>

      {/* Footer */}
      <Footer
        partners={apiData?.partner || []}
        footerServices={apiData?.footer_service || []}
        locations={apiData?.location || []}
      />
    </Router>
  );
}

export default App;
